import { useState, useEffect, useCallback } from 'react';
import { BarChart3, Users, AlertTriangle, TrendingDown, Activity, ArrowRight, Clock, Filter } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const STAGE_COLORS = {
  lead_nuevo: '#3B82F6', contactado: '#8B5CF6', calificado: '#F59E0B',
  en_visitas: '#10B981', en_negociacion: '#EF4444', cerrado: '#6BCB77', perdido: '#6B7280',
};
const STAGE_ORDER = ['lead_nuevo', 'contactado', 'calificado', 'en_visitas', 'en_negociacion', 'cerrado'];

const TABS = [
  { key: 'historico', label: 'Historico Conversion', icon: BarChart3 },
  { key: 'brokers', label: 'Conversion por Broker', icon: Users },
  { key: 'tiempos', label: 'Tiempos por Etapa', icon: Clock },
  { key: 'lost', label: 'Analisis Perdidos', icon: TrendingDown },
];
const PIE_COLORS = ['#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6', '#10B981', '#6BCB77', '#EC4899', '#A38560'];

export default function CRMDashboard() {
  const [data, setData] = useState(null);
  const [advanced, setAdvanced] = useState(null);
  const [tab, setTab] = useState('historico');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterBroker, setFilterBroker] = useState('');
  const [filterSource, setFilterSource] = useState('');

  const loadBase = useCallback(async () => {
    try { setData(await api.get('/crm/dashboard')); } catch { toast.error('Error cargando dashboard'); }
  }, []);

  const loadAdvanced = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (filterBroker) params.set('broker_id', filterBroker);
      if (filterSource) params.set('source', filterSource);
      setAdvanced(await api.get(`/crm/dashboard/advanced?${params.toString()}`));
    } catch { toast.error('Error cargando datos avanzados'); }
  }, [startDate, endDate, filterBroker, filterSource]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadAdvanced(); }, [loadAdvanced]);

  if (!data || !advanced) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#A38560] border-t-transparent rounded-full" /></div>;

  const { stage_stats, lost_analysis, health_overview } = data;
  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white h-9 rounded-lg text-xs";
  const selectCls = "bg-[#0A1F1A] border-[#1A3B32] text-white";

  return (
    <div className="space-y-5" data-testid="crm-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-outfit text-2xl font-semibold text-white">Dashboard CRM</h1>
        <button onClick={() => { loadBase(); loadAdvanced(); }} className="text-xs text-white/40 hover:text-white px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">Actualizar</button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Leads Activos" value={health_overview.total_active} icon={<Activity size={16} className="text-[#A38560]" />} />
        <StatCard label="Saludables" value={health_overview.green} icon={<div className="w-2.5 h-2.5 rounded-full bg-green-500" />} color="text-green-400" />
        <StatCard label="Requieren Atencion" value={health_overview.red} icon={<div className="w-2.5 h-2.5 rounded-full bg-red-500" />} color="text-red-400" />
        <StatCard label="Perdidos" value={lost_analysis.total} icon={<AlertTriangle size={16} className="text-gray-400" />} color="text-gray-400" />
      </div>

      {/* Capacity */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
        <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Capacidad por Etapa</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {STAGE_ORDER.filter(k => k !== 'cerrado').map(key => {
            const st = stage_stats[key];
            if (!st) return null;
            const pct = st.capacity ? Math.min((st.count / st.capacity) * 100, 100) : 0;
            const isFull = st.capacity && st.count >= st.capacity;
            return (
              <div key={key} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/60">{st.label}</span>
                  <span className={`text-xs font-mono ${isFull ? 'text-red-400' : 'text-white/40'}`}>{st.count}/{st.capacity || '-'}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: isFull ? '#EF4444' : STAGE_COLORS[key] }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-[#A38560]" />
          <p className="text-xs uppercase tracking-widest text-white/40">Filtros</p>
          {(startDate || endDate || filterBroker || filterSource) && (
            <button onClick={() => { setStartDate(''); setEndDate(''); setFilterBroker(''); setFilterSource(''); }}
              className="text-[10px] text-red-400 hover:text-red-300 ml-auto">Limpiar filtros</button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-white/30 uppercase mb-1 block">Desde</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} data-testid="filter-start" />
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase mb-1 block">Hasta</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inputCls} data-testid="filter-end" />
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase mb-1 block">Broker</label>
            <Select value={filterBroker} onValueChange={v => setFilterBroker(v === '_all' ? '' : v)}>
              <SelectTrigger className={`${inputCls} h-9`} data-testid="filter-broker"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent className={selectCls}>
                <SelectItem value="_all">Todos</SelectItem>
                {(advanced.brokers || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase mb-1 block">Fuente</label>
            <Select value={filterSource} onValueChange={v => setFilterSource(v === '_all' ? '' : v)}>
              <SelectTrigger className={`${inputCls} h-9`} data-testid="filter-source"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent className={selectCls}>
                <SelectItem value="_all">Todas</SelectItem>
                {(advanced.available_sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-[10px] text-white/20 mt-2">{advanced.total_leads} leads encontrados con estos filtros</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-[#0A1F1A] rounded-xl border border-[#1A3B32] p-1 w-fit overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${tab === t.key ? 'bg-[#A38560] text-[#03110D] font-medium' : 'text-white/50 hover:text-white'}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'historico' && <HistoricoView data={advanced} />}
      {tab === 'brokers' && <BrokersAdvancedView data={advanced} />}
      {tab === 'tiempos' && <TimesView data={advanced} />}
      {tab === 'lost' && <LostView analysis={lost_analysis} />}
    </div>
  );
}

function StatCard({ label, value, icon, color = 'text-white' }) {
  return (
    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-3">
      <div className="flex items-center gap-2 mb-1.5">{icon}<span className="text-[10px] text-white/40 uppercase tracking-wider">{label}</span></div>
      <p className={`text-xl font-outfit font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function HistoricoView({ data }) {
  const { historical_funnel, total_leads } = data;
  return (
    <div className="space-y-4">
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-white">Embudo Historico de Conversion</p>
          <span className="text-xs text-white/30">{total_leads} leads totales</span>
        </div>
        <p className="text-[10px] text-white/30 mb-4">Muestra cuantos leads pasaron por cada etapa (basado en historial, no etapa actual)</p>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={historical_funnel} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A3B32" />
              <XAxis dataKey="label" stroke="#ffffff40" tick={{ fontSize: 10 }} />
              <YAxis stroke="#ffffff40" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }}
                formatter={(v, n, p) => [`${v} leads`, `${p.payload.pct}% conversion de etapa anterior`]} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {historical_funnel.map((entry, i) => <Cell key={i} fill={STAGE_COLORS[entry.stage] || '#A38560'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Funnel steps with conversion rates */}
      <div className="flex flex-wrap gap-2 items-center">
        {historical_funnel.map((step, i) => (
          <div key={step.stage} className="flex items-center gap-2">
            <div className="bg-[#16302B] rounded-xl border border-white/5 px-4 py-3 text-center min-w-[90px]">
              <p className="text-[10px] text-white/40">{step.label}</p>
              <p className="text-lg font-outfit font-semibold text-white">{step.count}</p>
            </div>
            {i < historical_funnel.length - 1 && (
              <div className="flex flex-col items-center">
                <ArrowRight size={12} className="text-white/15" />
                <span className="text-[9px] text-[#A38560] font-medium">{historical_funnel[i + 1].pct}%</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BrokersAdvancedView({ data }) {
  const { broker_stage_metrics } = data;
  const stages = ['lead_nuevo', 'contactado', 'calificado', 'en_visitas', 'en_negociacion', 'cerrado'];
  const LABELS = { lead_nuevo: 'Lead Nuevo', contactado: 'Contactado', calificado: 'Calificado', en_visitas: 'En Visitas', en_negociacion: 'Negociacion', cerrado: 'Cerrado' };

  return (
    <div className="space-y-4">
      {broker_stage_metrics.length === 0 && (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-8 text-center">
          <p className="text-white/40">No hay datos de brokers para los filtros seleccionados.</p>
        </div>
      )}
      {broker_stage_metrics.map(b => (
        <div key={b.id} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6" data-testid={`broker-advanced-${b.id}`}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-medium text-white">{b.name}</p>
            <span className="text-xs text-white/30">{b.total} leads</span>
          </div>

          {/* Per-stage conversion bars */}
          <div className="space-y-2 mb-4">
            {stages.map(s => {
              const conv = b.stage_conversions[s] || { count: 0, pct: 0 };
              return (
                <div key={s} className="flex items-center gap-3">
                  <span className="text-[10px] text-white/40 w-20 text-right shrink-0">{LABELS[s]}</span>
                  <div className="flex-1 h-5 bg-white/5 rounded-lg overflow-hidden relative">
                    <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${conv.pct}%`, backgroundColor: STAGE_COLORS[s] }} />
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white/80 font-medium">
                      {conv.count} ({conv.pct}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stage-to-stage transition rates */}
          <div className="border-t border-white/5 pt-3">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Conversion entre etapas</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(b.stage_transitions).map(([key, pct]) => {
                const parts = key.split('_to_');
                const fromStage = parts[0] || '';
                const toStage = parts[1] || '';
                const fromLabel = (LABELS[fromStage] || fromStage).slice(0, 8);
                const toLabel = (LABELS[toStage] || toStage).slice(0, 8);
                return (
                  <div key={key} className="flex items-center gap-1 px-2 py-1.5 bg-[#16302B] rounded-lg border border-white/5 text-[10px]">
                    <span className="text-white/40">{fromLabel}</span>
                    <ArrowRight size={8} className="text-white/20" />
                    <span className="text-white/40">{toLabel}</span>
                    <span className={`font-medium ml-1 ${pct > 50 ? 'text-green-400' : pct > 20 ? 'text-[#A38560]' : 'text-red-400'}`}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TimesView({ data }) {
  const { avg_stage_times } = data;
  return (
    <div className="space-y-4">
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
        <p className="text-sm font-medium text-white mb-1">Tiempo Promedio por Etapa</p>
        <p className="text-[10px] text-white/30 mb-4">Promedio de dias que los clientes permanecen en cada etapa</p>
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={avg_stage_times} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1A3B32" />
              <XAxis dataKey="label" stroke="#ffffff40" tick={{ fontSize: 10 }} />
              <YAxis stroke="#ffffff40" tick={{ fontSize: 11 }} label={{ value: 'Dias', angle: -90, position: 'insideLeft', style: { fill: '#ffffff30', fontSize: 10 } }} />
              <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }}
                formatter={(v) => [`${v} dias`, 'Promedio']} />
              <Bar dataKey="avg_days" radius={[6, 6, 0, 0]}>
                {avg_stage_times.map((entry, i) => <Cell key={i} fill={STAGE_COLORS[entry.stage] || '#A38560'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {avg_stage_times.map(t => (
          <div key={t.stage} className="bg-[#16302B] rounded-xl border border-white/5 p-3 text-center">
            <div className="w-2 h-2 rounded-full mx-auto mb-2" style={{ backgroundColor: STAGE_COLORS[t.stage] }} />
            <p className="text-[10px] text-white/40 mb-1">{t.label}</p>
            <p className="text-lg font-outfit font-semibold text-white">{t.avg_days}d</p>
            <p className="text-[10px] text-white/20">{t.count} leads</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LostView({ analysis }) {
  const { by_reason, by_origin, by_source, total } = analysis;
  return (
    <div className="space-y-4">
      {total === 0 ? (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-8 text-center">
          <AlertTriangle size={32} className="text-white/20 mx-auto mb-3" />
          <p className="text-white/40">No hay leads perdidos aun.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
              <p className="text-sm font-medium text-white mb-4">Motivos de Perdida</p>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={by_reason} dataKey="count" nameKey="reason" cx="50%" cy="50%"
                      outerRadius={90} innerRadius={45} paddingAngle={2} label={({ count }) => `${count}`}>
                      {by_reason.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#ffffff80' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6">
              <p className="text-sm font-medium text-white mb-4">Etapa de Origen</p>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={by_origin} margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1A3B32" />
                    <XAxis dataKey="label" stroke="#ffffff40" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#ffffff40" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {by_origin.map((entry, i) => <Cell key={i} fill={STAGE_COLORS[entry.stage] || '#6B7280'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden">
            <table className="w-full"><thead><tr className="border-b border-white/5">
              <th className="text-left p-3 text-xs text-white/40 uppercase">Motivo</th>
              <th className="text-right p-3 text-xs text-white/40 uppercase">Cantidad</th>
              <th className="text-right p-3 text-xs text-white/40 uppercase">%</th>
            </tr></thead><tbody>
              {by_reason.map((r, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="p-3 text-sm text-white/70">{r.reason}</td>
                  <td className="p-3 text-sm text-white text-right font-medium">{r.count}</td>
                  <td className="p-3 text-sm text-white/40 text-right">{total > 0 ? Math.round(r.count / total * 100) : 0}%</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </>
      )}
    </div>
  );
}
